import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormField, form } from '@angular/forms/signals';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import {
  AirwallexValidationErrorResponse,
  BANK_COUNTRY_PRESETS,
  BankCountryPreset,
  BeneficiaryEntityType,
  CreateShoutBeneficiaryDto,
  CreatorPaymentDetailDto,
} from './creator-payment-details.types';
import { AdminCreatorPaymentDetailsService } from '../../services/admin-creator-payment-details.service';
import { CreatorPaymentDetailsChecklist } from './creator-payment-details-checklist/creator-payment-details-checklist';

// Re-exported for back-compat; canonical source is creator-payment-details.types.
export { BANK_COUNTRY_PRESETS, type BankCountryPreset };

@Component({
  selector: 'admin-tool-creator-payment-details-fix',
  imports: [CreatorPaymentDetailsChecklist, FormField],
  templateUrl: './creator-payment-details-fix.html',
  styleUrl: './creator-payment-details-fix.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorPaymentDetailsFix {
  private checklist = viewChild(CreatorPaymentDetailsChecklist);

  readonly countryPresets = BANK_COUNTRY_PRESETS;
  readonly entityTypeOptions: BeneficiaryEntityType[] = ['PERSONAL', 'COMPANY'];

  paymentDetail = signal<CreatorPaymentDetailDto | null>(null);
  validatePassed = signal(false);
  actionLoading = signal(false);

  beneficiaryModel = signal({
    entity_type: 'PERSONAL' as BeneficiaryEntityType,
    bankCountryPreset: 'GB',
    street_address: '',
    city: '',
    postcode: '',
    state: '',
    account_name: '',
    account_number: '',
    account_routing_value1: '',
    account_routing_value2: '',
    bank_account_category: 'Checking' as 'Checking' | 'Savings',
  });

  beneficiaryForm = form(this.beneficiaryModel);

  showBeneficiaryForm = computed(() => {
    const detail = this.paymentDetail();
    return !!detail && !detail.airwallexId;
  });

  selectedPreset = computed(() => {
    const code = this.beneficiaryModel().bankCountryPreset;
    return BANK_COUNTRY_PRESETS.find((p) => p.bankCountryCode === code) ?? BANK_COUNTRY_PRESETS[0];
  });

  isSwiftCountry = computed(() => {
    const code = this.selectedPreset().bankCountryCode;
    return code !== 'GB' && code !== 'US';
  });

  isUsCountry = computed(() => this.selectedPreset().bankCountryCode === 'US');

  showCreateButton = computed(() => this.showBeneficiaryForm() && this.validatePassed());

  private destroyRef = inject(DestroyRef);
  private paymentDetailsService = inject(AdminCreatorPaymentDetailsService);
  private alertService = inject(AlertService);
  private toastNotificationService = inject(ToastNotificationService);

  onDetailsLoaded(detail: CreatorPaymentDetailDto | null): void {
    this.paymentDetail.set(detail);
    this.validatePassed.set(false);
  }

  onCountryPresetChange(code: string): void {
    this.beneficiaryModel.update((m) => ({ ...m, bankCountryPreset: code }));
    this.validatePassed.set(false);
  }

  onBeneficiaryFieldChange(): void {
    this.validatePassed.set(false);
  }

  async runValidate(): Promise<void> {
    const creatorId = this.paymentDetail()?.creatorId?.trim();
    if (!creatorId) {
      return;
    }

    const body = this.buildBeneficiaryPayload();
    if (!body) {
      return;
    }

    this.actionLoading.set(true);

    this.paymentDetailsService
      .validateBeneficiaryAdmin(creatorId, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.actionLoading.set(false);
          const err = this.extractValidationError(response);
          if (err) {
            this.validatePassed.set(false);
            void this.toastNotificationService.alert({
              title: 'Validation failed',
              message: err,
              type: 'error',
              icon: 'error',
            });
            return;
          }

          this.validatePassed.set(true);
          void this.toastNotificationService.alert({
            title: 'Validation passed',
            message: 'Airwallex accepted these bank details. You can create the beneficiary.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: AirwallexValidationErrorResponse }) => {
          this.actionLoading.set(false);
          this.validatePassed.set(false);
          void this.toastNotificationService.alert({
            title: 'Validation failed',
            message: this.formatHttpError(err?.error) ?? 'Request failed.',
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  async runCreate(): Promise<void> {
    if (!this.validatePassed()) {
      void this.toastNotificationService.alert({
        title: 'Validate first',
        message: 'Run validation successfully before creating the Airwallex beneficiary.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const creatorId = this.paymentDetail()?.creatorId?.trim();
    if (!creatorId) {
      return;
    }

    const body = this.buildBeneficiaryPayload();
    if (!body) {
      return;
    }

    const confirmed = await this.alertService.confirm({
      title: 'Create Airwallex beneficiary',
      message: `Create beneficiary for creator ${creatorId}? This sets airwallexId on their payment detail row.`,
      confirmText: 'Create',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    if (!confirmed) {
      return;
    }

    this.actionLoading.set(true);

    this.paymentDetailsService
      .createBeneficiaryAdmin(creatorId, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.actionLoading.set(false);
          this.validatePassed.set(false);
          this.paymentDetail.set(created);
          this.checklist()?.loadDetails();
          void this.toastNotificationService.alert({
            title: 'Beneficiary created',
            message: created.airwallexId
              ? `airwallexId: ${created.airwallexId}`
              : 'Beneficiary request completed.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: AirwallexValidationErrorResponse }) => {
          this.actionLoading.set(false);
          void this.toastNotificationService.alert({
            title: 'Create failed',
            message: this.formatHttpError(err?.error) ?? 'Request failed.',
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private buildBeneficiaryPayload(): CreateShoutBeneficiaryDto | null {
    const m = this.beneficiaryModel();
    const preset = BANK_COUNTRY_PRESETS.find((p) => p.bankCountryCode === m.bankCountryPreset);
    if (!preset) {
      return null;
    }

    if (!m.account_name.trim() || !m.account_number.trim()) {
      void this.toastNotificationService.alert({
        title: 'Missing bank fields',
        message: 'Account name and account number (or IBAN) are required.',
        type: 'error',
        icon: 'error',
      });
      return null;
    }

    const bank_details: CreateShoutBeneficiaryDto['bank_details'] = {
      account_name: m.account_name.trim(),
      account_number: m.account_number.trim().replace(/\s/g, ''),
      account_routing_value1: m.account_routing_value1.trim(),
      bank_country_code: preset.bankCountryCode,
      account_currency: preset.currency,
    };

    if (this.isSwiftCountry() && m.account_routing_value2.trim()) {
      bank_details.account_routing_value2 = m.account_routing_value2.trim();
    }

    if (this.isUsCountry()) {
      bank_details.bank_account_category = m.bank_account_category;
    }

    const payload: CreateShoutBeneficiaryDto = {
      entity_type: m.entity_type,
      bank_details,
    };

    if (this.isUsCountry() || this.isSwiftCountry()) {
      if (!m.city.trim() || !m.street_address.trim() || !m.postcode.trim()) {
        void this.toastNotificationService.alert({
          title: 'Address required',
          message: 'Street, city, and postcode are required for this country.',
          type: 'error',
          icon: 'error',
        });
        return null;
      }

      payload.address = {
        street_address: m.street_address.trim(),
        city: m.city.trim(),
        postcode: m.postcode.trim(),
        state: m.state.trim() || m.city.trim(),
        country_code: preset.bankCountryCode,
      };
    }

    return payload;
  }

  private extractValidationError(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const body = response as AirwallexValidationErrorResponse;
    if (body.code === 'validation_failed') {
      return this.formatHttpError(body);
    }
    return null;
  }

  private formatHttpError(body: AirwallexValidationErrorResponse | undefined): string | null {
    if (!body) {
      return null;
    }
    const first = body.details?.errors?.[0];
    if (first?.message) {
      return first.message;
    }
    return body.message ?? null;
  }
}
